import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { push } from 'connected-react-router';
import moment from 'moment';
import { formatDatePartialFormat, getYearNumber, getMonthNumber } from '@widergy/web-utils/lib/date';
import { range } from '@widergy/web-utils/lib/array';
import { padTwo } from '@widergy/web-utils/lib/normalize';

import { ROUTES } from 'constants/routes';
import numeral from 'config/numeral';
import { appConfig } from 'config/customization';
import { currencyUnit, consumptionUnit } from 'constants/units';
import BillActions from 'redux/bill/actions';
import { actionCreators as accountActions, accountPropTypes } from 'redux/accountHandlers';
import { actionCreators as userFormActions } from 'redux/userFormHandlers';
import { actionCreators as paymentActions } from 'redux/paymentHandlers';
import { billSelector } from 'redux/bill/selectors';
import { currentUserPropTypes } from 'redux/authHandlers';
import { PROCEDURE } from 'constants/userForms';
import WithoutAccounts from 'app/components/WithoutAccounts';
import { gaTrackPageView } from 'services/AnalyticsService';

import AccountStatus from './layout';

class AccountStatusContainer extends Component {
  componentDidMount() {
    this.props.dispatch(userFormActions.getFormCategories(PROCEDURE));
    // When you change account from another component
    if (this.props.currentAccount && !this.props.currentAccount.accountUpdated) {
      this.props.dispatch(
        BillActions.getConsumptionHistory(
          this.props.currentAccountId,
          appConfig.accountStatus.consumptionHistoryPeriods
        )
      );
      this.props.dispatch(accountActions.getAccountAsync(this.props.currentAccountId));
      if (appConfig.bills.lastBill) this.props.dispatch(BillActions.getLastBill(this.props.currentAccountId));
    }
    gaTrackPageView(ROUTES.ACCOUNT_STATUS_PAGEVIEW);
  }

  componentDidUpdate(prevProps) {
    // When you change account in this component
    if (prevProps.currentAccountId !== this.props.currentAccountId) {
      this.props.dispatch(
        BillActions.getConsumptionHistory(
          this.props.currentAccountId,
          appConfig.accountStatus.consumptionHistoryPeriods
        )
      );
      this.props.dispatch(accountActions.getAccountAsync(this.props.currentAccountId));
      if (appConfig.bills.lastBill) this.props.dispatch(BillActions.getLastBill(this.props.currentAccountId));
    }
    if (prevProps.paymentRequest !== this.props.paymentRequest && this.props.paymentRequest) {
      this.props.dispatch(accountActions.getAccountAsync(this.props.currentAccountId));
    }
  }

  handleChangeAccount = account => this.props.dispatch(accountActions.changeCurrentAccount(account));

  handleGoToAccounts = () => this.props.dispatch(push(ROUTES.ACCOUNTS));

  handleDownloadBill = billId =>
    this.props.dispatch(
      appConfig.bills.billDownloadOnPDF
        ? BillActions.getPrintedBillPDF(billId)
        : BillActions.getPrintedBill(billId, 'currentBill')
    );

  handleDownloadPaymentDocument = () =>
    this.props.dispatch(
      appConfig.payments.paymentDocumentDownloadOnPDF
        ? paymentActions.getPaymentDocumentPDF(this.props.currentAccount.client_number)
        : paymentActions.getPaymentDocument(this.props.currentAccount.client_number)
    );

  render() {
    return (
      <AccountStatus
        {...this.props}
        onChangeAccount={this.handleChangeAccount}
        onGoToAccounts={this.handleGoToAccounts}
        onDownloadBill={this.handleDownloadBill}
        onDownloadPaymentDocument={this.handleDownloadPaymentDocument}
      />
    );
  }
}

const currentBillSelector = createSelector(
  store => store.bill.consumptionHistory,
  store => store.bill.currentBill,
  (consumptionHistory, bill) => {
    if (consumptionHistory && consumptionHistory.bills && consumptionHistory.bills.length > 0) {
      const history = [...consumptionHistory.bills];
      const currentBill = history.reverse()[0];
      return billSelector(currentBill, bill, consumptionHistory);
    }
    return null;
  }
);

function buildBillProgressData(lastBills, currentBills) {
  const progress = range(12).map(month => ({
    ...month,
    month: padTwo(1 + month)
  }));
  return progress.map(month => {
    const lastBill = lastBills.find(bill => padTwo(bill.period) === month.month);
    const currentBill = currentBills.find(bill => padTwo(bill.period) === month.month);
    return {
      ...month,
      year: currentBill ? 'current' : 'last',
      currentYear: currentBill ? `${getYearNumber(currentBill.period)}` : undefined,
      currentConsumption: currentBill && currentBill.consumption,
      currentAmount: currentBill && currentBill.amount,
      currentIssuedDate: currentBill && currentBill.issued_on,
      currentStartDate: currentBill && currentBill.period_started_on,
      currentEndDate: currentBill && currentBill.period_ended_on,
      currentDueDate: currentBill && currentBill.first_expiration_on,
      currentPeriod: currentBill && currentBill.period,
      lastYear: lastBill ? `${getYearNumber(lastBill.period)}` : undefined,
      lastConsumption: lastBill && lastBill.consumption,
      lastAmount: lastBill && lastBill.amount,
      lastIssuedDate: lastBill && lastBill.issued_on,
      lastStartDate: lastBill && lastBill.period_started_on,
      lastEndDate: lastBill && lastBill.period_ended_on,
      lastDueDate: lastBill && lastBill.first_expiration_on,
      lastPeriod: lastBill && lastBill.period
    };
  });
}

const consumptionHistorySelector = createSelector(
  store => store.bill.consumptionHistory,
  consumptionHistory => {
    const yearsToDisplay = [];
    const bills =
      consumptionHistory &&
      consumptionHistory.bills.map(bill => {
        const year = getYearNumber(bill.period);
        if (!yearsToDisplay.includes(year)) yearsToDisplay.push(year);
        return { ...bill, month: bill.period.slice(-2), year };
      });
    return {
      yearsToDisplay: yearsToDisplay
        .sort((a, b) => a < b)
        .slice(0, 3)
        .reverse(),
      bills
    };
  }
);

const billProgressSelector = createSelector(
  store => store.bill.billProgress,
  billProgress => {
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;
    if (billProgress) {
      let progress = {
        currencyUnit,
        consumptionUnit,
        currentYearAccumulated: `${currentYear} 0 ${consumptionUnit}`,
        existsLastYearConsumption: billProgress.last_year_accumulated > 0,
        lastYearAccumulated: `${lastYear} 0 ${consumptionUnit}`,
        consumptionDifference: `${numeral(billProgress.consumption_difference).format()} ${consumptionUnit}`,
        moneySaving: numeral(billProgress.money_savings).format('$0,0.[00]'),
        carbonImpact: `${numeral(billProgress.carbon_impact / 1000).format('0.[00]')} ppm`,
        percentageSaved:
          billProgress.last_year_accumulated > 0
            ? numeral(
                Math.round(
                  (1 - billProgress.current_year_accumulated / billProgress.last_year_accumulated) * 100
                )
              ).format()
            : 0,
        progressData: buildBillProgressData(billProgress.last_year_bills, billProgress.current_year_bills)
      };
      if (billProgress.current_year_bills[0]) {
        const currentYearData = {
          currentYear,
          currentMonth: padTwo(
            getMonthNumber(billProgress.current_year_bills[billProgress.current_year_bills.length - 1].period)
          ),
          currentConsumption:
            billProgress.current_year_bills[billProgress.current_year_bills.length - 1].consumption,
          currentYearAccumulated: `${currentYear} ${numeral(billProgress.current_year_accumulated).format()}
          ${consumptionUnit}`,
          currentAmount: billProgress.current_year_bills[billProgress.current_year_bills.length - 1].amount,
          currentIssuedDate: moment(
            billProgress.current_year_bills[billProgress.current_year_bills.length - 1].issued_on
          ).format('L'),
          currentStartDate: moment(
            billProgress.current_year_bills[billProgress.current_year_bills.length - 1].period_started_on
          ).format('L'),
          currentEndDate: moment(
            billProgress.current_year_bills[billProgress.current_year_bills.length - 1].period_ended_on
          ).format('L'),
          currentDueDate: moment(
            billProgress.current_year_bills[billProgress.current_year_bills.length - 1].first_expiration_on
          ).format('L'),
          currentPeriod: `${formatDatePartialFormat(
            billProgress.current_year_bills[billProgress.current_year_bills.length - 1].period
          )}`
        };
        progress = { ...progress, ...currentYearData };
      }
      if (billProgress.last_year_bills[0]) {
        const lastYearData = {
          lastYear,
          lastYearAccumulated: `${lastYear} ${numeral(billProgress.last_year_accumulated).format()}
           ${consumptionUnit}`
        };
        progress = { ...progress, ...lastYearData };
      }
      return progress;
    }
    return null;
  }
);

const mapStateToProps = store => ({
  currentAccount: store.account.currentAccount,
  currentAccountId: store.account.currentAccount && store.account.currentAccount.id,
  accounts: store.account.accounts,
  user: store.auth.currentUser,
  paymentRequest: store.payment.paymentRequest,
  currentBill: appConfig.bills.lastBill
    ? billSelector(store.bill.lastBill, store.bill.currentBill, store.bill.consumptionHistory)
    : currentBillSelector(store),
  billProgress: billProgressSelector(store),
  consumptionHistory: consumptionHistorySelector(store),
  loadingAccount: store.account.accountsLoading,
  accountsError: store.account.accountsError,
  loadingBill:
    store.bill.billProgressLoading ||
    store.bill.consumptionHistoryLoading ||
    store.bill.billsLoading ||
    store.account.accountsLoading
});

AccountStatusContainer.propTypes = {
  currentAccount: accountPropTypes,
  accounts: accountPropTypes,
  currentAccountId: PropTypes.number,
  loadingAccount: PropTypes.bool,
  paymentRequest: PropTypes.bool,
  user: currentUserPropTypes
};

export default connect(mapStateToProps)(
  WithoutAccounts({
    shouldShowMessage: props => props.accounts.length === 0 && !props.loadingAccount,
    accountsError: props => props.accountsError
  })(AccountStatusContainer)
);
